import { HttpClient, HttpDownloadProgressEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { isContactIdShaped } from './contact-id';
import { Injectable } from '@angular/core';
import { EventPhoto, GallerySet, Photo/*, PhotoEvent*/ } from '@pixomaticc/ngx-ui';
import Utils from '@uzenith360/utils'
import { HandledHttpResponse, HttpError, httpRetry, PaginatedResult, httpRunner, httpStatus } from '@uzenith360/http-utils';
import { Observable, catchError, of, map, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import EventUserCode from './event-user-code.interface';
import InitPaymentData from './init-payment-data.interface';
import paymentStatus from './payment-status.enum';
import OfflineInitPaymentData from './offline-init-payment-data.interface';
import GalleryReview from './rating-and-review-bottom-sheet/gallery-review.interface';
import SelectGalleryListPhotoResult from '../portfolio/fetch-data-from-db/types/select-gallery-list-photo-result.type';

@Injectable({
  providedIn: 'root'
})
export class EventPhotosService {
  // private static creativesAppTokenStoreId: string = 'iop';

  constructor(private http: HttpClient) { }

  public requestMoreAction(
    action: 'frame' | 'photo book' | 'print',
    contactId: string,
    photoURLs: string[],
    photographerId: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      action,
      photoURLs,
      photographerId,
    };

    return this.http.post(`${environment.usersBaseUrl}/request-more-action`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public requestMoreActionGalleryNotification(
    action: 'frame' | 'photo book' | 'print',
    contactId: string,
    eventId: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      action,
      eventId,
    };

    return this.http.post(`${environment.usersBaseUrl}/request-more-action-gallery-notification`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public requestMoreActionPhotoNotification(
    action: 'frame' | 'photo book' | 'print',
    contactId: string,
    eventId: string,
    photoId: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      action,
      eventId,
      photoId,
    };

    return this.http.post(`${environment.usersBaseUrl}/request-more-action-photo-notification`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  /**
   * The guest's "I've paid" notice — now to PAYMENTS, which owns the record, not to users' open route.
   *
   * Owner ruling 2026-10-22 ~17:42Z (estate `OWNER-RULINGS.md:12305`, `061e877`). Contract read at
   * payments' source, `post-cut/offline-payment-notify @ a51884f5`:
   *   POST client-notify-offline-payment/{offlinePaymentId}  body { contactId, eventId }
   *   200 { data: true, outcome: SENT | UNCERTAIN | ALREADY_NOTIFIED }
   *   503 { reason: 'NOTICE_NOT_SENT' }            users refused BEFORE sending — a retry is SAFE
   *   404 { reason: 'OFFLINE_PAYMENT_NOT_FOUND' }  not pending, OR a malformed id, OR not owned here
   *
   * ⚠ THE RETRY LIST IS NARROWED ON PURPOSE — BUT NOT FOR THE REASON THIS NOTE ORIGINALLY GAVE, and
   * the wrong version is kept visible because it is the one a reader half-remembers. It said *"a bare
   * `httpRetry()` would repeat a permanent 404"*. **That is false, and the installed default says so:**
   *     httpRetry(maxTries = 5, delayMs = 700, retryableStatuses = [0, 500, 502, 503, 504])
   * — 404 is NOT in it, so a bare call never retried a 404 and the narrowing cannot have fixed that.
   * Caught by the first review; the same false sentence was in three artifacts at once.
   *
   * WHAT THE NARROWING ACTUALLY DOES, stated because none of it was: maxTries 5 → 3, delay 700 → 1000
   * ms, and **500 dropped** from the retryable set. The 500 is the only load-bearing part: payments
   * write their `notifiedAt` claim BEFORE calling users, so a 500 can mean the claim already exists
   * while the outcome is unknown — repeating it buys nothing a second tap would not, and payments'
   * at-most-once is the thing that protects the guest, not our retry. 0/502/503/504 stay because
   * payments state 503 is a refusal BEFORE sending, and a transport failure may mean the request never
   * arrived.
   *
   * ⚠ WHY THE WHOLE BODY COMES BACK rather than a boolean: the OUTCOME decides the sentence. 200 means
   * "stop retrying", NOT "it arrived" — see `offline-notice-outcome.ts`. A boolean cannot carry that,
   * and the old copy claimed delivery on all three outcomes.
   *
   * `code9` is gone from this call; there is no equivalent. payments identify the record from the path
   * id plus the contact and event in the body, and check it against their OWN row.
   */
  public offlinePaymentNotification(
    offlinePaymentId: string,
    contactId: string,
    eventId: string,
  ): Observable<HandledHttpResponse<{ ok: boolean, outcome?: string, body?: unknown }>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      eventId,
    };

    return this.http.post(
      `${environment.paymentsBaseUrl}/client-notify-offline-payment/${encodeURIComponent(offlinePaymentId)}`,
      body,
    )
      .pipe(
        httpRetry(3, 1000, [0, 502, 503, 504]),
        catchError((err: HttpErrorResponse) => of({ __failed: true, status: err.status, body: err.error })),
        map<any, HandledHttpResponse<{ ok: boolean, outcome?: string, body?: unknown }>>(
          (res: any) => (
            res?.__failed
              ? { data: { ok: false, body: res.body }, message: res.body?.message, statusCode: res.status }
              : { data: { ok: res?.data === true, outcome: res?.outcome }, message: '', statusCode: 200 }
          ),
        ),
      );
  }

  public addGalleryReview(
    contactId: string,
    eventId: string,
    rating: number,
    review?: string,
    tags?: string[],
  ): Observable<HandledHttpResponse<GalleryReview>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      eventId,
      rating
    };

    if (!!review) {
      body['review'] = review;
    }

    if (!!tags) {
      body['tags'] = tags;
    }

    return this.http.post(`${environment.eventsBaseUrl}/gallery-review`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: GalleryReview, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as GalleryReview,
            },
          )
        }),
        map<{ data: GalleryReview | undefined, statusCode: number, message: any }, HandledHttpResponse<GalleryReview>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public updateCollaborationEvent(
    contactId: string,
    eventId: string,
    galleryPhotosListId?: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      eventId,
    };

    if (galleryPhotosListId !== undefined) {
      body['galleryPhotosListId'] = galleryPhotosListId;
    }

    return this.http.patch(`${environment.eventsBaseUrl}/collaboration`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

/**
   * ⚠ DEAD CODE. NOTHING CALLS THIS. **Do not wire it up without the owner.**
   *
   * Written at his request, relayed 2026-10-24: *"Add a comment or something so future agent would know
   * the route is unused"*. Kept here rather than only in the ledger because this is where someone
   * reaching for a "fetch the event over HTTP" method will actually be standing.
   *
   * MEASURED 2026-10-24, not inferred. Every reference to the name under `src/`, grepped with no
   * include filter so templates were covered, is a declaration or an unrelated same-named method:
   *   event-photos.resolver.ts:59   calls the RESOLVER'S OWN private `fetchEvent`, not this one
   *   event-photos.resolver.ts:76   that private method's declaration
   *   event-photos.service.ts:280   this declaration
   * `this.eventPhotosService.fetchEvent(...)` appears NOWHERE. (Control, since a grep negative is a claim
   * about the grep: `fetchSelectedPhotosInSet`, a method that IS called, shows up at its call sites.)
   *
   * WHAT RUNS INSTEAD — `event-photos.resolver.ts` branches on platform and never makes this request:
   *   :81  isPlatformServer   -> :83 getFetchEventFromDb(), the dynamic import at :39 — straight to Mongo
   *   :104 isPlatformBrowser  -> :105 transferState.get(galleryDataKey, …) — reads the SSR-serialised
   *                              state, so even client-side navigation issues NO request
   *
   * AND THE ROUTE IS BROKEN ANYWAY: events' `GET /2/fetch-by-user-code` **502s for MULTIPLE-mode
   * galleries, which is the DEFAULT** — it calls photos' `is-photo-published-since-last-match`, a route
   * photos has never deployed, and its catch then crashes on `instanceof got.HTTPError` under got 14.
   * Cause measured by the coordinator, estate `a011920`. SINGLE mode skips the call and returns 200.
   * So wiring this up would break the default gallery, not merely add a call.
   *
   * ⚠ AND `fetch-by-user-code` IS PRESENT IN THE SERVED BUNDLE — `chunk-F3M27V7Q.js` on the 2026-10-24
   * staging build — because this is a public method on an injectable, so it is bundled rather than
   * tree-shaken. **Bundled is not called.** Finding that string in a bundle is not evidence the app hits
   * the route, and it should not reopen this.
   *
   * ⚠ LATENT, UNREPRODUCED: the `catchError` below reads `err.error.message`, which would TypeError
   * INSIDE the handler if `err.error` were null (a 502 with an empty or non-JSON body), erroring the
   * observable instead of yielding the fallback. Unreachable while nothing calls this; fix it before
   * any caller is added.
   */
  public fetchEvent(userCode: string, setId: string | undefined, setTitle: string | undefined, origin: string | undefined)
    : Observable<HandledHttpResponse<Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }>> {
    const params: { [key: string]: string | number } = {
      userCode,
    };

    if (!!setId) {
      params['setId'] = setId
    }

    if (!!setTitle) {
      params['setTitle'] = setTitle;
    }

    if (!!origin) {
      params['origin'] = origin;
    }

    return this.http.get(`${environment.eventsBaseUrl}/fetch-by-user-code`, { params })
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message ?? 'Problem completing this request, please try again',
              data: {} as Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean },
            },
          )
        }),
        map<{ data: Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }, statusCode: number, message: any }, HandledHttpResponse<Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }>>(
          ({ data: eventUserData, statusCode, message }) => (
            {
              data: {
                ...eventUserData,
                offlinePaymentAmount: eventUserData.offlinePaymentAmount
                  && Utils.roundOff2Dp(eventUserData.offlinePaymentAmount / 100),
              },
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public fetchEventForGalleryPreview(id: string, type: 'cover' | 'gallery' | 'both', date: string)
    : Observable<HandledHttpResponse<Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }>> {
    const params: { [key: string]: string | number } = {
      i: id, t: type, d: date,
    };

    return this.http.get(`${environment.eventsBaseUrl}/fetch-fgp`, { params/*, headers: this.getCreativesAuth()*/ })
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message ?? 'Problem completing this request, please try again',
              data: {} as Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean },
            },
          )
        }),
        map<{ data: Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }, statusCode: number, message: any }, HandledHttpResponse<Omit<EventUserCode, 'downloadPIN'> & { downloadPIN: boolean }>>(
          ({ data: eventUserData, statusCode, message }) => (
            {
              data: eventUserData,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public saveSelfie(
    name: string,
    height: number,
    width: number,
    contactId: string,
    // eventId: string,
    photoURL: string,
  ): Observable<HandledHttpResponse<string>> {
    const body: { [key: string]: string | number } = {
      name,
      height,
      width,
      contactId,
      // eventId,
      photoURL,
    };

    return this.http.post(`${environment.photosBaseUrl}/user-selfie`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: string, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as string,
            },
          )
        }),
        map<{ data: string | undefined, statusCode: number, message: any }, HandledHttpResponse<string>>(
          ({ data: photoId, statusCode, message }) =>
          (
            {
              data: photoId,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public savePhoto(
    name: string,
    height: number,
    width: number,
    highResURL: string
  ): Observable<HandledHttpResponse<EventPhoto>> {
    const body: { [key: string]: string | number } = {
      name,
      height,
      width,
      highResURL,
    };

    return this.http.post(`${environment.photosBaseUrl}/create`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: EventPhoto, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as EventPhoto,
            },
          )
        }),
        map<{ data: EventPhoto | undefined, statusCode: number, message: any }, HandledHttpResponse<EventPhoto>>(
          ({ data: photoEvent, statusCode, message }) =>
          (
            {
              data: photoEvent,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public removePhotoIdMatch(
    contactId: string,
    eventId: string,
    setId: string | undefined,
    photoId: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number } = {
      contactId,
      eventId,
      photoId,
    };

    if (!!setId) {
      body['setId'] = setId;
    }

    return this.http.delete(`${environment.photosBaseUrl}/remove-photo-id-match`, { body })
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: false as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public cancelOfflinePayment(
    offlinePaymentId: string,
  ): Observable<HandledHttpResponse<boolean>> {
    return this.http.delete(`${environment.paymentsBaseUrl}/client-cancel-offline-payment/${offlinePaymentId}`)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: false as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: iscancelled, statusCode, message }) =>
          (
            {
              data: iscancelled,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public fetchPhotoMatchesPhotos(
    contactId: string,
    eventId: string,
    setId: string | undefined,
    reference: string,
    isDownloadHighRes: boolean,
  ): Observable<HandledHttpResponse<string[]>> {
    const params: { [key: string]: string | number } = {
      contactId,
      eventId,
      reference,
      resId: isDownloadHighRes ? '6639b4c0228872c8af70c635' : Utils.generateRandomString(24, '0123456789abcdef'),
    };

    if (!!setId) {
      params['setId'] = setId;
    }

    return this.http.get(`${environment.photosBaseUrl}/fetch-photo-matches-photos`, { params })
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: string[], statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: [] as string[],
            },
          )
        }),
        map<{ data: string[] | undefined, statusCode: number, message: any }, HandledHttpResponse<string[]>>(
          ({ data: photos, statusCode, message }) =>
          (
            {
              data: photos, //photos?.map((photo) => ({ ...photo, ignoreSelectMode: !!photo.highResURL })),
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public fetchPhotoMatches(
    contactId: string,
    eventId: string,
    setId: string | undefined,
    page: number,
    limit: number,
    reference?: string,
  ): Observable<HandledHttpResponse<Photo[]>> {
    const params: { [key: string]: string | number } = {
      contactId,
      eventId,
      page,
      limit,
    };

    if (!!reference) {
      params['reference'] = reference;
    }

    if (!!setId) {
      params['setId'] = setId;
    }

    return this.http.get(`${environment.eventsBaseUrl}/fetch-photo-matches`, { params })
      .pipe(
        httpRetry(15),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: Photo[], statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: [] as Photo[],
            },
          )
        }),
        map<{ data: Photo[] | undefined, statusCode: number, message: any }, HandledHttpResponse<Photo[]>>(
          ({ data: photos, statusCode, message }) =>
          (
            {
              data: photos/*?.map((photo) => ({ ...photo, ignoreSelectMode: !!photo.highResURL }))*/,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public listForGalleryPreview(
    photographerId: string,
    eventId: string,
    page: number,
    limit: number,
    setId?: string,
  ): Observable<HandledHttpResponse<PaginatedResult<Photo[]>>> {
    const params: { [key: string]: string | number } = {
      eventId,
      page,
      limit,
      key: photographerId.split('').reverse().join(''),
    };

    if (!!setId) {
      params['id'] = setId;
    }

    return httpRunner(
      this.http.get(`${environment.photosBaseUrl}/list-for-gallery-preview`, { params }),
      'Problem completing this request, please try again',
      undefined,
      15
    );
  }

  public galleryListSignup(
    photographerId: string,
    eventId: string,
    fullName: string,
    phone?: string,
    email?: string,
  ): Observable<HandledHttpResponse<{ id: string, extraSelectionAmount?: number, currency?: string, selectionsLimit?: number, photos: Pick<Photo, '_id' | 'galleryPhotosList'>[] }>> {
    const body: { [key: string]: string | number } = {
      id: `${photographerId}${eventId}`,
      fullName,
    };

    if (!!phone) {
      body['phone'] = phone;
    }

    if (!!email) {
      body['email'] = email;
    }

    return httpRunner(
      this.http.put(`${environment.eventsBaseUrl}/gallery-list-signup`, body),
      'Problem completing this request, please try again',
      undefined,
      15
    );
  }

  public uploadUserProfilePhotoId(
    id: string,
    profilePhotoId?: string,
    previousProfilePhotoId?: string,
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: string | number } = {
      id,
    };

    if (!!profilePhotoId) {
      body['profilePhotoId'] = profilePhotoId;
    }

    if (!!previousProfilePhotoId) {
      body['previousProfilePhotoId'] = previousProfilePhotoId;
    }

    return this.http.patch(`${environment.usersBaseUrl}/users/update-profile-photo-id`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: false as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public fetchUserPhotosFromEvent(
    setId: string | undefined,
    eventId: string,
    contactId: string,
  ): Observable<HandledHttpResponse<Photo[]>> {
    const params: { [key: string]: string | number } = {
      wbn: eventId,
      hni: contactId,
    };

    if (!!setId) {
      params['setId'] = setId;
    }

    return this.http.get(`${environment.photosBaseUrl}/fetch-user-photos-from-event`, { params })
      .pipe(
        httpRetry(undefined, 5000),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: EventPhoto[], statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: [] as EventPhoto[],
            },
          )
        }),
        map<{ data: EventPhoto[] | undefined, statusCode: number, message: any }, HandledHttpResponse<EventPhoto[]>>(
          ({ data: photoEvent, statusCode, message }) =>
          (
            {
              data: photoEvent,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
        map(
          ({ message, statusCode, data }) => ({
            message,
            statusCode,
            data: data?.map(
              (
                {
                  _id,
                  type,
                  width,
                  height,
                  mimetype,
                  highResURL,
                  midResURL,
                  lowResURL,
                  name,
                  createdAt,
                },
              ) => (
                {
                  height,
                  name,
                  timestamp: !!createdAt ? new Date(createdAt).getTime() : Date.now(),
                  width,
                  type,
                  mimetype,
                  highResURL,
                  midResURL,
                  lowResURL,
                  // ignoreSelectMode: !!highResURL,
                  _id,

                }
              ),
            )
          })
        ),
      );
  }

  public editMeta(
    id: string, isClientDownloaded?: boolean, isClientSelected?: boolean, isClientReviewed?: boolean, isSkipClientReview?: boolean
  ): Observable<HandledHttpResponse<boolean>> {
    const body: { [key: string]: boolean | string } = {
      id,
    };

    if (isClientDownloaded !== undefined) {
      body['isClientDownloaded'] = isClientDownloaded;
    }

    if (isClientSelected !== undefined) {
      body['isClientSelected'] = isClientSelected;
    }

    if (isClientReviewed !== undefined) {
      body['isClientReviewed'] = isClientReviewed;
    }

    if (isSkipClientReview !== undefined) {
      body['isSkipClientReview'] = isSkipClientReview;
    }

    return this.http.patch(`${environment.eventsBaseUrl}/edit-meta`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: boolean, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: false as boolean,
            },
          )
        }),
        map<{ data: boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<boolean>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  /**
 * A client's review screen must show only THEIR OWN picks.
 *
 * `contactId` requested by photos-service 2026-10-22, consistent with the per-contact match records
 * ruling (estate `OWNER-RULINGS.md` 2026-10-22 ~15:33Z, `ce4081f`) — that ruling covers photos' OWN
 * four readers and the production-db index, NOT this query param, so this half is ordinary work in
 * this repo agreed with the service that owns the endpoint. Lands in the post-cut batch.
 *
 * Without it photos merges selections per photo as a stopgap, which shows client B client A's picks.
 *
 * ⚠ AN ABSENT id OMITS THE PARAM RATHER THAN REFUSING THE CALL, and that is deliberate. photos
 * ignores the param today, so omitting it is EXACTLY today's behaviour and introduces no regression —
 * whereas refusing would be a NEW blank-review-screen failure invented by this change. **The caller
 * reports the absence instead**, because the fallback is the leaky path and a leaky path that is
 * silent is the one nobody fixes.
 *
 * ⚠ VALIDATED, NOT FORWARDED BLINDLY: anything that is not 24 hex is treated as absent. A malformed
 * value would be a filter photos cannot honour, and a filter the server drops looks exactly like no
 * filter at all — so it must not reach the wire claiming to be one.
 */
  public fetchSelectedPhotosInSet(
    setId: string,
    contactId?: string,
  ): Observable<HandledHttpResponse<Photo[]>> {
    const scoped: string | undefined = isContactIdShaped(contactId) ? contactId : undefined;
    const url: string = `${environment.photosBaseUrl}/client-gallery-set/${setId}`
      + (!!scoped ? `?contactId=${encodeURIComponent(scoped)}` : '');

    return httpRunner(
      this.http.get(url),
      undefined,
      undefined,
      10
    );
  }

  public fetchGalleryPhotosListPhotos(
    galleryPhotosListId: string,
  ): Observable<HandledHttpResponse<(Photo & { set: Pick<GallerySet, "title"> })[]>> {
    return httpRunner(
      this.http.get(`${environment.photosBaseUrl}/gallery-photos-list/${galleryPhotosListId}`),
      undefined,
      (result: unknown) => (result as { photos: (Photo & { set: Pick<GallerySet, "title"> })[] })?.photos,
      10
    );
  }

  public selectMatchedUserPhoto(
    contactId: string,
    eventId: string,
    setId: string | undefined,
    photoId: string,
    isSelected: boolean,
  ): Observable<HandledHttpResponse<SelectGalleryListPhotoResult>> {
    const body: { [key: string]: string | number | boolean | string[] } = {
      contactId,
      eventId,
      photoId,
      isSelected,
    };

    if (!!setId) {
      body['setId'] = setId;
    }

    return httpRunner(
      this.http.patch(`${environment.photosBaseUrl}/select-matched-user-photo`, body)
    );
  }

  public selectGalleryListPhoto(
    eventId: string,
    photoId: string,
    isSelected: boolean,
    galleryPhotosListId: string,
    isUpdateonly: boolean,
  ): Observable<HandledHttpResponse<SelectGalleryListPhotoResult>> {
    const body: { [key: string]: string | number | boolean | string[] } = {
      galleryPhotosListId,
      eventId,
      photoId,
      isSelected,
      isUpdateonly,
    };

    return httpRunner(
      this.http.patch(`${environment.photosBaseUrl}/select-gallery-list-photo`, body),
      undefined,
      undefined,
      10
    );
  }

  public commentMatchedUserPhoto(
    photoId: string,
    text: string | undefined,
    contactId?: string,
    eventId?: string,
    setId?: string,
    galleryPhotosListId?: string,
  ): Observable<HandledHttpResponse<string>> {
    const body: { [key: string]: string | number | string[] } = {
      photoId,
    };

    // Means we want to delete the comment
    if (text !== undefined) {
      body['text'] = text;
    }

    if (!!setId) {
      body['setId'] = setId;
    }

    if (!!contactId) {
      body['contactId'] = contactId;
    }

    if (!!eventId) {
      body['eventId'] = eventId;
    }

    if (!!galleryPhotosListId) {
      body['galleryPhotosListId'] = galleryPhotosListId;
    }

    return this.http.post(`${environment.photosBaseUrl}/comment-matched-user-photo`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: string, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as string,
            },
          )
        }),
        map<{ data: string | boolean | undefined, statusCode: number, message: any }, HandledHttpResponse<string>>(
          ({ data: newCommentId, statusCode, message }) =>
          (
            {
              data: newCommentId === true
                ? 'true'
                : (
                  newCommentId === false
                    ? ''
                    : newCommentId
                ),
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public downloadMatchedUserPhotos(
    contactId: string,
    eventId: string,
    setId: string | undefined = Utils.generateRandomString(24, '0123456789abcdef'),
    photoIds: string[],
    email?: string,
    downloadPIN?: string,
  ): Observable<HandledHttpResponse<string>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      eventId,
      photoIds,
    };

    if (!!setId) {
      body['setId'] = setId;
    }

    if (!!email) {
      body['email'] = email;
    }

    if (!!downloadPIN) {
      body['downloadPIN'] = downloadPIN;
    }

    return this.http.post(`${environment.photosBaseUrl}/download-matched-user-photos`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: string, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as string,
            },
          )
        }),
        map<{ data: string | undefined, statusCode: number, message: any }, HandledHttpResponse<string>>(
          ({ data: newDownloadId, statusCode, message }) =>
          (
            {
              data: newDownloadId,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  /**
   * The zip producer. Two shapes come back and the caller must handle both, so `data` is deliberately
   * `unknown` rather than `string`:
   *   - WITHOUT `wantProgress`: today's bare string — a `.zip` URL when ready, else `<reversed uid>|<date>`.
   *   - WITH `wantProgress` (sends `p: "1"`): photos' object `{ status, ready, total, links, parts }`.
   * `p` goes ONLY on polls, never on the build request, matching the studio's wire shape as recorded in
   * `test/fixtures/zip-polls-B-2parts-20261008.json` (`requestBodyKeysEveryCall`).
   *
   * ⚠ 503 IS EXCLUDED FROM `httpRetry` ON PURPOSE, and this is not a style choice. photos answers a failed
   * registry read with **503 `{ status: "UNKNOWN" }`, which is a RETRY instruction for the CLIENT**, and the
   * zip-progress module owns that cadence: 3, 6, 12, 24 s then every 30 s, with a soft notice after two
   * minutes of it. `httpRetry`'s defaults are `(5, 700, [0, 500, 502, 503, 504])`, so leaving 503 in would
   * retry it five times at 700 ms INSIDE this method and then surface a terminal error — the module would
   * never see an UNKNOWN answer, its backoff would never run and its notice would never appear. The 503 is
   * passed through with `data: { status: 'UNKNOWN' }` so the module can do its job; every other retryable
   * status keeps the old behaviour.
   *
   * The status is NOT rewritten to 200: the caller decides what a 503 means, and a service that lies about
   * a status code is worse than one that reports it.
   */
  public zipAndDownloadFiles(
    filesString: string,
    trustyDigits: string,
    contactIdEventIdPhotographerId: string,
    fileName: string,
    wantProgress?: boolean,
  ): Observable<HandledHttpResponse<unknown>> {
    const body: { [key: string]: string | number | string[] } = {
      i: filesString,
      _: trustyDigits,
      b: contactIdEventIdPhotographerId,
    };

    if (!!fileName) {
      body['o'] = fileName;
    }

    if (!!wantProgress) {
      body['p'] = '1';
    }

    return this.http.post(`${environment.photosBaseUrl}/zip-and-download-files`, body)
      .pipe(
        httpRetry(5, 700, [0, 500, 502, 504]),
        catchError((err: HttpErrorResponse) => {
          // photos' 503 is an instruction, not a failure: hand the module the shape it recognises.
          if (err.status === httpStatus.SERVICE_UNAVAILABLE) {
            return of({ statusCode: err.status, message: '', data: { status: 'UNKNOWN' } as unknown });
          }

          // `err.error` is null on a transport failure, so this must be optional — it was not, and an
          // offline guest would have thrown a TypeError inside the error handler itself.
          return of({ statusCode: err.status, message: err.error?.message, data: {} as unknown });
        }),
        map<{ data: unknown, statusCode: number, message: any }, HandledHttpResponse<unknown>>(
          ({ data, statusCode, message }) => ({ data, message, statusCode: statusCode ?? 200 }),
        ),
      );
  }

  public offlineInitPayment(
    contactId: string,
    eventId: string,
    photoIds: string[] | undefined,
    photographerId: string,
    unpaidPhotosExist: boolean,
  ): Observable<HandledHttpResponse<OfflineInitPaymentData>> {
    const body: { [key: string]: boolean | string | number | string[] } = {
      contactId,
      eventId,
      unpaidPhotosExist,
      photographerId,
      type: 'PHOTO',
    };

    if (!!photoIds) {
      body['data'] = photoIds;
    }

    return this.http.post(`${environment.paymentsBaseUrl}/init-offline`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: OfflineInitPaymentData, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as OfflineInitPaymentData,
            },
          )
        }),
        map<{ data: OfflineInitPaymentData | undefined, statusCode: number, message: any }, HandledHttpResponse<OfflineInitPaymentData>>(
          ({ data: offlineInitPaymentData, statusCode, message }) =>
          (
            {
              data: !!offlineInitPaymentData
                ? {
                  ...offlineInitPaymentData,
                  amount: offlineInitPaymentData.amount
                    && Utils.roundOff2Dp(offlineInitPaymentData.amount / 100),
                }
                : { id: '', amount: 0, bankAccount: {} as OfflineInitPaymentData['bankAccount'] },
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public initPayment(
    contactId: string,
    eventId: string,
    photoIds: string[] | undefined,
    photographerId: string,
  ): Observable<HandledHttpResponse<InitPaymentData>> {
    const body: { [key: string]: string | number | string[] } = {
      contactId,
      eventId,
      photographerId,
      // data: photoIds,
      type: 'PHOTO',
    };

    if (!!photoIds) {
      body['data'] = photoIds;
    }

    return this.http.post(`${environment.paymentsBaseUrl}/init`, body)
      .pipe(
        httpRetry(),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: InitPaymentData, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as InitPaymentData,
            },
          )
        }),
        map<{ data: InitPaymentData | undefined, statusCode: number, message: any }, HandledHttpResponse<InitPaymentData>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public verifyPayment(
    reference: string,
  ): Observable<HandledHttpResponse<paymentStatus>> {
    return this.http.get(`${environment.paymentsBaseUrl}/verify-paystack/${reference}`)
      .pipe(
        httpRetry(undefined, 5000),
        catchError((err: HttpErrorResponse, caught: Observable<{ data: paymentStatus, statusCode: number, message: any; }>) => {
          return of(
            {
              statusCode: err.status,
              message: err.error.message,
              data: {} as paymentStatus,
            },
          )
        }),
        map<{ data: paymentStatus | undefined, statusCode: number, message: any }, HandledHttpResponse<paymentStatus>>(
          ({ data: isUpdated, statusCode, message }) =>
          (
            {
              data: isUpdated,
              message,
              statusCode: statusCode ?? 200,
            }
          ),
        ),
      );
  }

  public downloadFile(fileURL: string): Observable<HttpResponse<void> | HttpDownloadProgressEvent> {
    return this.http.get(
      fileURL,
      {
        responseType: 'blob',
        headers: {
          // stop HTTPInterceptors from catching it and adding headers
          'skip-interceptors': "true",
          // stop angular service worker from intercepting this request
          // wierdly, this issue only showed up on mobile
          'ngsw-bypass': "true",
        },
        reportProgress: true,
        observe: 'events',
      }
    ).pipe(
      httpRetry(5, undefined, [0, 502, 503, 504, 500, 499]/*1, 2000, [0, 502, 500, 499]*/),
      catchError((err: HttpErrorResponse, caught: Observable<HttpResponse<void> | HttpDownloadProgressEvent>) => {
        switch (err.status) {
          case 500:
            return throwError(() => new HttpError('Problem uploading file, please try again', err.status));
          case 0:
          default:
            return throwError(
              () => new HttpError(
                (err.error?.message?.join && err.error?.message?.join(', ')) ?? err.error?.message ?? err?.message ?? 'Problem uploading file, please check your internet connection and try again',
                err.status,
              ),
            );
        };
      }),
    );
  }
}
